var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { modeParameters } from "@jtarrio/signals/demod/modes.js";
import { RrWindow, WindowDelegate } from "../../ui/controls/window.js";
import * as Icons from "../../ui/icons.js";
import { BaseStyle } from "../../ui/styles.js";
import "../../ui/controls/frequency-input.js";
import "../../ui/controls/window.js";
let RrPresets = class RrPresets extends WindowDelegate(LitElement) {
    constructor() {
        super(...arguments);
        this.inline = false;
        this.hidden = false;
        this.tunedFrequency = 88500000;
        this.scale = 1000;
        this.tuningStep = 1000;
        this.scheme = "WBFM";
        this.bandwidth = 150000;
        this.stereo = true;
        this.squelch = 0;
        this.gain = null;
        this.sortColumn = "frequency";
        this.presets = [];
        this.sortedIndices = [];
        this.editorOpen = false;
        this.editorContent = {
            name: "",
            tunedFrequency: this.tunedFrequency,
            scale: this.scale,
            tuningStep: this.tuningStep,
            scheme: this.scheme,
            bandwidth: this.bandwidth,
            stereo: this.stereo,
            squelch: this.squelch,
            gain: this.gain,
        };
    }
    static get styles() {
        return [
            BaseStyle,
            css `
        table {
          border-collapse: collapse;
          width: 100%;
          cursor: default;
        }

        tr.active {
          background: #7bd;
        }

        tr:nth-child(even) {
          background: #eee;
          &.active {
            background: #6bd;
          }
        }

        td:nth-child(n + 2) {
          width: 0;
        }

        th,
        td {
          text-wrap: nowrap;
          padding: 0.4ex 0.8ex;
          cursor: pointer;
        }

        td:first-child {
          max-width: 15ex;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        svg {
          vertical-align: text-top;
        }

        a svg {
          fill: #22e;
        }

        .buttonIllustration {
          position: relative;
          top: 0.5ex;
          margin-top: -2ex;
          z-index: 0;
        }

        #preset-editor {
          bottom: inherit;
          right: inherit;
          margin: auto;

          div:first-child {
            margin-bottom: 1ex;
          }
          div:last-child {
            margin-top: 1ex;
          }
        }

        @media (prefers-color-scheme: dark) {
          tr.active {
            background: #135;
          }

          tr:nth-child(even) {
            background: #333;
            &.active {
              background: #147;
            }
          }

          a svg {
            fill: #55f;
          }
        }
      `,
        ];
    }
    render() {
        return html `<rr-window
        label=${this.selectedIndex === undefined
            ? "Presets"
            : `Current preset: ${this.presets[this.selectedIndex].name}`}
        id="presets"
        class=${this.inline ? "inline" : ""}
        closeable
        .closed=${this.closed}
        .position=${this.position}
        .size=${this.size}
        .fixed=${this.inline}
        .resizeable=${true}
      >
        <button
          slot="label-left"
          .disabled=${this.selectedIndex !== undefined}
          @click=${this.onAddClick}
        >
          ${Icons.Add}
        </button>
        <table>
          <tr>
            <th id="name" @click=${this.onHeaderClick}>
              Name${this.getSortArrow("name")}
            </th>
            <th id="frequency" @click=${this.onHeaderClick}>
              Frequency${this.getSortArrow("frequency")}
            </th>
            <th id="mode" @click=${this.onHeaderClick}>
              Mode${this.getSortArrow("mode")}
            </th>
            <th></th>
          </tr>
          ${this.sortedIndices.map((index) => html `<tr
                data-index=${index}
                class=${index == this.selectedIndex ? "active" : ""}
                @click=${this.onRowClick}
              >
                <td>${this.presets[index].name}</td>
                <td>
                  ${humanFrequency(this.presets[index].tunedFrequency, this.presets[index].scale)}
                </td>
                <td>${this.presets[index].scheme}</td>
                <td>
                  <a href="javascript:0" @click=${this.onRowEditClick}
                    >${Icons.Edit}</a
                  ><a href="javascript:0" @click=${this.onRowDeleteClick}
                    >${Icons.Delete}</a
                  >
                </td>
              </tr>`)}
        </table>
        ${this.presets.length == 0
            ? html `<p style="max-width: 50ex">
              You can use Presets to flip quickly to your favorite stations or
              frequencies. Click the
              <button disabled class="buttonIllustration">${Icons.Add}</button>
              button on this window's top left corner to add the current
              frequency to the presets.
            </p>`
            : nothing}
      </rr-window>

      <rr-window
        id="preset-editor"
        .label=${this.editorTitle || ""}
        closeable
        modal
        .closed=${!this.editorOpen}
        @rr-window-open=${this.onEditorOpen}
        @rr-window-closed=${this.onEditorClosed}
      >
        <div>
          <label for="presetName">Name: </label
          ><input
            id="presetName"
            type="text"
            .value=${this.editorContent.name}
            @keydown=${this.onEditorNameKeydown}
            @input=${this.onEditorNameChange}
          />
        </div>
        <div>
          Frequency:
          <b
            >${humanFrequency(this.editorContent.tunedFrequency, this.editorContent.scale)}</b
          >, Tuning step:
          <b>${humanFrequency(this.editorContent.tuningStep, 1)}</b>
        </div>
        <div>
          Modulation:
          <b
            >${this.editorContent.scheme}${modeParameters(this.editorContent.scheme).hasStereo()
            ? this.editorContent.stereo
                ? " Stereo"
                : " Mono"
            : nothing}</b
          >${modeParameters(this.editorContent.scheme).hasBandwidth()
            ? html `, Bandwidth:
                <b>${humanFrequency(this.editorContent.bandwidth, 1)}</b>`
            : nothing}
        </div>
        <div>
          Gain:
          <b
            >${this.editorContent.gain === null
            ? "Auto"
            : this.editorContent.gain}</b
          >${modeParameters(this.editorContent.scheme).hasSquelch()
            ? html `, Squelch: <b>${this.editorContent.squelch}</b>`
            : nothing}
        </div>
        ${this.editorIndex !== undefined
            ? html `<div>
              <button @click=${this.onEditorReplaceClick}>
                Replace with current settings
              </button>
            </div>`
            : nothing}
        <div>
          <button
            .disabled=${this.editorValidationError !== undefined}
            @click=${this.onEditorSaveClick}
          >
            Save</button
          >${this.editorValidationError !== undefined
            ? html ` <i>${this.editorValidationError}</i>`
            : nothing}
        </div>
      </rr-window>`;
    }
    willUpdate(changed) {
        super.willUpdate(changed);
        if (changed.has("presets") || changed.has("sortColumn")) {
            this.updatePresetLists();
        }
        this.findSelectedIndex();
    }
    updatePresetLists() {
        let sortedIndices = [...this.presets.keys()];
        sortedIndices.sort(this.getSortFormula());
        this.sortedIndices = sortedIndices;
    }
    onAddClick(e) {
        this.editorTitle = "New Preset";
        this.editorIndex = undefined;
        this.editorContent = {
            name: "",
            tunedFrequency: this.tunedFrequency,
            scale: this.scale,
            tuningStep: this.tuningStep,
            scheme: this.scheme,
            bandwidth: this.bandwidth,
            stereo: this.stereo,
            squelch: this.squelch,
            gain: this.gain,
        };
        this.checkValidEditor();
        this.editorOpen = true;
    }
    onEditorNameKeydown(e) {
        if (e.key == "Enter") {
            e.preventDefault();
            this.onEditorSaveClick();
        }
        else if (e.key == "Escape") {
            e.preventDefault();
            this.onEditorClosed();
        }
    }
    onEditorNameChange(e) {
        let target = e.target;
        let value = target.value;
        this.editorContent.name = value;
        this.checkValidEditor();
    }
    onEditorReplaceClick() {
        this.editorContent = {
            name: this.editorContent.name,
            tunedFrequency: this.tunedFrequency,
            scale: this.scale,
            tuningStep: this.tuningStep,
            scheme: this.scheme,
            bandwidth: this.bandwidth,
            stereo: this.stereo,
            squelch: this.squelch,
            gain: this.gain,
        };
        this.checkValidEditor();
    }
    checkValidEditor() {
        if (this.editorContent.name == "") {
            this.editorValidationError = "Preset name is empty";
            return;
        }
        let idx = this.presets.findIndex((p) => p.name == this.editorContent.name);
        if (idx >= 0 && idx != this.editorIndex) {
            this.editorValidationError = "There is another preset with that name";
            return;
        }
        idx = this.presets.findIndex((p) => arePresetsEqual(p, this.editorContent));
        if (idx >= 0 && idx != this.editorIndex) {
            this.editorValidationError = `There is an identical preset: ${this.presets[idx].name}`;
            return;
        }
        this.editorValidationError = undefined;
    }
    onEditorSaveClick() {
        let presets = [...this.presets];
        if (this.editorIndex === undefined || this.editorIndex >= presets.length) {
            presets.push({ ...this.editorContent });
        }
        else {
            presets[this.editorIndex] = { ...this.editorContent };
        }
        this.editorOpen = false;
        this.presets = presets;
        this.dispatchEvent(new PresetsChangedEvent());
    }
    onEditorOpen() {
        if (this.presetName)
            this.presetName.focus();
    }
    onEditorClosed() {
        this.editorOpen = false;
    }
    onRowClick(e) {
        let index = this.getIndex(e);
        if (index === undefined)
            return;
        this.selectedIndex = index;
        this.dispatchEvent(new PresetSelectedEvent());
    }
    onRowEditClick(e) {
        e.stopPropagation();
        const index = this.getIndex(e);
        if (index === undefined)
            return;
        let preset = { ...this.presets[index] };
        this.editorTitle = `Editing Preset "${preset.name}"`;
        this.editorIndex = index;
        this.editorContent = preset;
        this.checkValidEditor();
        this.editorOpen = true;
    }
    onRowDeleteClick(e) {
        e.stopPropagation();
        const index = this.getIndex(e);
        if (index === undefined)
            return;
        let presets = [...this.presets];
        presets.splice(index, 1);
        this.selectedIndex = undefined;
        this.presets = presets;
        this.dispatchEvent(new PresetsChangedEvent());
    }
    getIndex(e) {
        let target = e.target;
        while (target != null && target.tagName != "TR") {
            target = target.parentElement;
        }
        if (target == null)
            return;
        let index = Number(target.dataset["index"]);
        if (isNaN(index))
            return;
        return index;
    }
    onHeaderClick(e) {
        const id = e.currentTarget.id;
        const minusid = `-${id}`;
        if (this.sortColumn === id) {
            this.sortColumn = minusid;
        }
        else {
            this.sortColumn = id;
        }
        this.dispatchEvent(new PresetsSortedEvent());
    }
    getSortArrow(name) {
        if (this.sortColumn === name) {
            return Icons.SortDown;
        }
        else if (this.sortColumn === `-${name}`) {
            return Icons.SortUp;
        }
        return nothing;
    }
    getSortFormula() {
        let sortColumn = this.sortColumn || "frequency";
        let desc = sortColumn[0] == "-";
        if (desc)
            sortColumn = sortColumn.substring(1);
        let sortFormula;
        switch (sortColumn) {
            case "name":
                sortFormula = (a, b) => this.presets[a].name.localeCompare(this.presets[b].name);
                break;
            case "mode":
                sortFormula = (a, b) => this.presets[a].scheme.localeCompare(this.presets[b].scheme);
                break;
            default:
                sortFormula = (a, b) => this.presets[a].tunedFrequency - this.presets[b].tunedFrequency;
                break;
        }
        if (desc)
            return (a, b) => sortFormula(b, a);
        return sortFormula;
    }
    findSelectedIndex() {
        let idx = this.presets.findIndex((p) => arePresetsEqual(p, this));
        if (idx < 0) {
            this.selectedIndex = undefined;
        }
        else {
            this.selectedIndex = idx;
        }
    }
};
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Boolean)
], RrPresets.prototype, "inline", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Boolean)
], RrPresets.prototype, "hidden", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrPresets.prototype, "tunedFrequency", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrPresets.prototype, "scale", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrPresets.prototype, "tuningStep", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", String)
], RrPresets.prototype, "scheme", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrPresets.prototype, "bandwidth", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Boolean)
], RrPresets.prototype, "stereo", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrPresets.prototype, "squelch", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Object)
], RrPresets.prototype, "gain", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Number)
], RrPresets.prototype, "selectedIndex", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", String)
], RrPresets.prototype, "sortColumn", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Array)
], RrPresets.prototype, "presets", void 0);
__decorate([
    state(),
    __metadata("design:type", Array)
], RrPresets.prototype, "sortedIndices", void 0);
__decorate([
    state(),
    __metadata("design:type", String)
], RrPresets.prototype, "editorTitle", void 0);
__decorate([
    state(),
    __metadata("design:type", Boolean)
], RrPresets.prototype, "editorOpen", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], RrPresets.prototype, "editorIndex", void 0);
__decorate([
    state(),
    __metadata("design:type", String)
], RrPresets.prototype, "editorValidationError", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], RrPresets.prototype, "editorContent", void 0);
__decorate([
    query("#presets"),
    __metadata("design:type", typeof (_a = typeof RrWindow !== "undefined" && RrWindow) === "function" ? _a : Object)
], RrPresets.prototype, "window", void 0);
__decorate([
    query("#presetName"),
    __metadata("design:type", typeof (_b = typeof HTMLInputElement !== "undefined" && HTMLInputElement) === "function" ? _b : Object)
], RrPresets.prototype, "presetName", void 0);
RrPresets = __decorate([
    customElement("rr-presets")
], RrPresets);
export { RrPresets };
export class PresetSelectedEvent extends Event {
    constructor() {
        super("rr-preset-selected", { bubbles: true, composed: true });
    }
}
export class PresetsChangedEvent extends Event {
    constructor() {
        super("rr-presets-changed", { bubbles: true, composed: true });
    }
}
export class PresetsSortedEvent extends Event {
    constructor() {
        super("rr-presets-sorted", { bubbles: true, composed: true });
    }
}
function arePresetsEqual(a, b) {
    return (a.tunedFrequency === b.tunedFrequency &&
        a.scale === b.scale &&
        a.tuningStep === b.tuningStep &&
        a.bandwidth === b.bandwidth &&
        a.stereo === b.stereo &&
        a.squelch === b.squelch &&
        a.gain === b.gain);
}
function humanFrequency(freq, scale) {
    switch (scale) {
        case 1000:
            return `${String(freq / 1000)} kHz`;
        case 1000000:
            return `${String(freq / 1000000)} MHz`;
        default:
            return `${String(freq)} Hz`;
    }
}
